import React from 'react';
import styles from './EfficientGraphExperiments.module.css';

const datasets = [
  {
    name: 'OGB-MolHIV',
    scale: '414k molecules',
    task: 'Binary classification',
  },
  {
    name: 'OGB-MolPCBA',
    scale: '437k molecules',
    task: 'Multi-label classification',
  },
];

const baselines = ['GCN', 'GAT', 'MPNN', 'GIN'];

const metrics = [
  { dataset: 'MolHIV', metric: 'ROC-AUC' },
  { dataset: 'MolPCBA', metric: 'Average Precision (AP)' },
];

const results = [
  {
    name: 'MolHIV performance',
    scoreLabel: 'MolHIV ROC-AUC',
    rows: [
      ['GCN', '77.1', '8.2'],
      ['GAT', '78.5', '10.1'],
      ['MPNN', '80.2', '12.4'],
      ['GIN', '80.7', '11.8'],
      ['FastMolGNN', '82.3', '7.1'],
    ],
  },
  {
    name: 'MolPCBA performance',
    scoreLabel: 'MolPCBA AP (%)',
    rows: [
      ['GCN', '23.4', '9.0'],
      ['GAT', '25.0', '11.0'],
      ['MPNN', '28.2', '13.5'],
      ['GIN', '29.1', '12.8'],
      ['FastMolGNN', '30.5', '8.7'],
    ],
  },
];

function DatasetCard({ name, scale, task }) {
  return (
    <article className={styles.datasetCard}>
      <h4>{name}</h4>
      <dl>
        <div>
          <dt>Scale</dt>
          <dd>{scale}</dd>
        </div>
        <div>
          <dt>Task</dt>
          <dd>{task}</dd>
        </div>
      </dl>
    </article>
  );
}

function ResultTable({ name, rows, scoreLabel }) {
  const captionId = `${name.toLowerCase().replace(/\s+/g, '-')}-caption`;

  return (
    <figure className={styles.resultPanel}>
      <figcaption id={captionId}>{name}</figcaption>
      <div
        className={styles.tableScroll}
        role="region"
        aria-label={`Scrollable ${name} table`}
        tabIndex="0"
      >
        <table aria-labelledby={captionId}>
          <thead>
            <tr>
              <th scope="col">Model</th>
              <th scope="col">{scoreLabel}</th>
              <th scope="col">Time (h)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([model, score, time]) => {
              const isFastMolGNN = model === 'FastMolGNN';

              return (
                <tr
                  className={isFastMolGNN ? styles.fastMolRow : undefined}
                  key={model}
                >
                  <th scope="row">
                    {isFastMolGNN ? <strong>{model}</strong> : model}
                  </th>
                  <td>{score}</td>
                  <td>{time}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

export default function EfficientGraphExperiments() {
  return (
    <section className={styles.experiments} aria-labelledby="experiments-heading">
      <div className={styles.header}>
        <p>Section 5</p>
        <h2 id="experiments-heading">5 Experiments</h2>
        <p>
          Two Open Graph Benchmark datasets test binary and multi-label molecular
          property prediction against four peer GNN models.
        </p>
      </div>

      <div className={styles.setup}>
        <section className={styles.setupSection} aria-labelledby="datasets-heading">
          <h3 id="datasets-heading">Dataset &amp; task</h3>
          <div className={styles.datasetGrid}>
            {datasets.map((dataset) => (
              <DatasetCard key={dataset.name} {...dataset} />
            ))}
          </div>
        </section>

        <section className={styles.setupSection} aria-labelledby="baselines-heading">
          <h3 id="baselines-heading">Peer baselines</h3>
          <ul className={styles.baselineStrip} aria-label="Peer-model comparison">
            {baselines.map((baseline) => (
              <li key={baseline}>{baseline}</li>
            ))}
          </ul>
        </section>

        <section
          className={styles.setupSection}
          aria-labelledby="metrics-heading"
          role="group"
        >
          <h3 id="metrics-heading">Evaluation metrics</h3>
          <div className={styles.metricGrid}>
            {metrics.map(({ dataset, metric }) => (
              <article className={styles.metricCard} key={dataset}>
                <p>{dataset}</p>
                <strong>{metric}</strong>
              </article>
            ))}
          </div>
        </section>
      </div>

      <section
        className={styles.results}
        aria-label="Benchmark results"
        role="group"
      >
        <div className={styles.resultsHeading}>
          <p>Reported benchmark performance</p>
          <h3 id="results-heading">Results</h3>
        </div>
        <div className={styles.resultsGrid}>
          {results.map((result) => (
            <ResultTable key={result.name} {...result} />
          ))}
        </div>
      </section>

      <aside className={styles.takeaway} aria-label="Results takeaway">
        <strong>Takeaway</strong>
        <p>
          FastMolGNN records the highest reported score and shortest training time
          in both tables: 82.3 ROC-AUC in 7.1 h on MolHIV, and 30.5 AP in 8.7 h on
          MolPCBA.
        </p>
      </aside>
    </section>
  );
}
